import { useEffect, useId, useRef } from 'react';
import type { Hold } from '../../types/climb';
import type { BoardHand } from '../../types/device';
import { holdShownInMode, type HoldsDisplayMode } from './holds-display-mode';
import { HAND_MARK_COLOR, handMarkArc } from './board-grid/roles';
import { circuitHandsOf, circuitNumbers } from './circuit';
import { photoHoldRole } from './photo-hold-roles';
import { holdPath, holdStrokeWidth } from './hold-outline';
import { climbPhotoPaint, type ClimbPhotoEffect } from './climb-photo-effect';

interface HoldsOverlayProps {
  holds?: Hold[] | null;
  /** Natural (intrinsic) dimensions of the underlying image, from img.onLoad. */
  naturalWidth: number | null;
  naturalHeight: number | null;
  /**
   * Must match the object-fit of the underlying <img>. The SVG mirrors it via
   * preserveAspectRatio so holds stay aligned: 'cover' → slice, 'contain' → meet.
   */
  objectFit?: 'cover' | 'contain';
  /** Which subset of holds to draw. Defaults to 'all'. */
  mode?: HoldsDisplayMode;
  /**
   * Whether the holds are a **circuit** — the order they are climbed in rather
   * than a set. Each circle then wears the number of the move it is, which is the
   * only way the order can be read off a photograph.
   */
  circuit?: boolean;
  /**
   * The hold a circuit run is on, by its place in `holds`. The rest dim down to
   * it and it flashes, which on a photograph is what the board does with its LEDs
   * — see BoardGridDisplay.
   */
  currentHold?: number | null;
  /** Play the "drawing the circle" animation. Defaults to true. */
  animate?: boolean;
  /**
   * Show only part of the image, as a rect in natural pixels. Used by the
   * route-setting board's thumbnail, which is cropped to the start holds;
   * omit it to cover the whole image, which is what every other caller wants.
   */
  viewBox?: { x: number; y: number; width: number; height: number } | null;
  /**
   * How the photograph is being shown — see `climb-photo-effect.ts`. The parent
   * pushes the wall back (drains its colour, dims it, throws it out of focus);
   * this gives the climb's own holds back, cut out of the same picture.
   *
   * **Independent of `mode`.** The rings come and go with the lightbulb; this
   * does not, because it *is* the climb when the rings are off. A reset wall
   * with every circuit on it is unreadable without either.
   */
  effect?: ClimbPhotoEffect;
  /** The same picture the parent is showing, for the colour to be cut out of. */
  imageSrc?: string | null;
  className?: string;
}

/**
 * Draws climb holds over an image: each hold's own outline where it has one, and
 * otherwise the circle it is (see `holdPath`). Holds are stored as fractions
 * (0-1) of the image's natural dimensions; the SVG uses the natural size as its
 * viewBox and the same fit rule as the image, so the marks land exactly on the
 * rendered pixels at any size. The parent element must be `relative`.
 *
 * The "drawing" animation is driven with the Web Animations API (not a CSS
 * class) so it survives the Tailwind/Lightning CSS build, and replays whenever
 * the visible set changes.
 */
export function HoldsOverlay({ holds, naturalWidth, naturalHeight, objectFit = 'cover', mode = 'all', circuit = false, currentHold = null, animate = true, viewBox, effect = 'none', imageSrc, className }: HoldsOverlayProps) {
  const groupRef = useRef<SVGGElement>(null);
  // One clip (and one glow) per instance: a feed draws many of these on one page.
  const instanceId = useId();
  const colourClipId = `${instanceId}-colour`;
  const bloomId = `${instanceId}-bloom`;
  const paint = climbPhotoPaint(effect);

  const W = naturalWidth ?? 0;
  const H = naturalHeight ?? 0;
  // The numbers are counted over the *whole* circuit, not over what is drawn:
  // hiding everything but the start and the top must not renumber the run.
  const numbers = holds?.length && circuit ? circuitNumbers(holds, photoHoldRole) : [];
  const visible = holds?.length && W && H
    ? holds
        .map((hold, index) => ({
          hold,
          order: numbers[index] ?? null,
          // The hand(s) the hold is taken with, where the circuit forces them
          // — the server wrote them onto the holds (Boulder#assign_photo_hands).
          hands: circuitHandsOf(hold, photoHoldRole),
          // The hold the run is on, and the ones it has dimmed down to. A foot is
          // never dimmed: the climber's foot stays there for the whole of the run,
          // which is why the wall keeps its LED on too.
          on: currentHold === index,
          dimmed:
            currentHold !== null && currentHold !== index && photoHoldRole(hold) !== 'foot',
        }))
        .filter(({ hold }) => holdShownInMode(hold, mode))
    : [];
  const visibleCount = visible.length;

  useEffect(() => {
    if (!animate) return;
    const group = groupRef.current;
    if (!group) return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    // The holds only: the hand marks are paths too, and live in a group of
    // their own outside this one.
    const marks = Array.from(group.querySelectorAll('path'));

    // Stagger in semantic order: start (green) → intermediate (blue/other) → top
    // (red), regardless of vertical position. Stable sort keeps ties in DOM order.
    const priority = (color: string | null) => (color === 'green' ? 0 : color === 'red' ? 2 : 1);
    const stagger: number[] = [];
    marks
      .map((_, i) => i)
      .sort((a, b) => priority(marks[a].getAttribute('stroke')) - priority(marks[b].getAttribute('stroke')))
      .forEach((originalIndex, rank) => { stagger[originalIndex] = rank; });

    marks.forEach((el, i) => {
      // jsdom and reduced-motion: just show the fully drawn circle.
      if (reduceMotion || typeof el.animate !== 'function') {
        el.style.strokeDashoffset = '0';
        return;
      }
      // Cancel any in-flight animation so rapid mode toggles don't stack.
      el.getAnimations?.().forEach((a) => a.cancel());
      el.animate(
        [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }],
        { duration: 600, delay: stagger[i] * 90, easing: 'ease-out', fill: 'forwards' }
      );
    });
  }, [animate, mode, visibleCount, W, H]);

  // **The colour layer survives the lightbulb.** With the rings off there is
  // nothing left to draw, except where the wall has been pushed back — there
  // the holds in colour *are* the climb.
  const paintsColour = !!paint.holds && !!imageSrc && W > 0 && H > 0 && (holds?.length ?? 0) > 0;
  if (!visibleCount && !paintsColour) return null;

  const box = viewBox ?? { x: 0, y: 0, width: W, height: H };
  // Stroke in viewBox units so it scales with the image, measured against the
  // long edge of what is actually shown so a zoomed-in crop does not get fat
  // marks. A circle keeps the share it has always had; a traced shape is drawn
  // against its own size instead, because the picture's share is thicker than
  // some holds are wide — see `holdStrokeWidth`.
  const referenceEdge = Math.max(box.width, box.height);
  const preserveAspectRatio = objectFit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice';

  return (
    <svg
      className={`absolute inset-0 w-full h-full pointer-events-none ${className ?? ''}`}
      viewBox={`${box.x} ${box.y} ${box.width} ${box.height}`}
      preserveAspectRatio={preserveAspectRatio}
      aria-hidden="true"
    >
      {/* The same photograph again, clipped to this climb's holds and pushed a
          little past life — the colour (or the light, or the focus) given back
          to the plastic that is the climb, over a wall that has been pushed
          back. Under the rings, so a ring is never covered by it. */}
      {paintsColour && (
        <>
          <defs>
            <clipPath id={colourClipId}>
              {(holds ?? []).map((hold, index) => (
                <path key={index} d={holdPath(hold, W, H)} />
              ))}
            </clipPath>
            {/* What makes a lit hold look lit: its own light spilling onto the
                wall around it. An SVG filter rather than a CSS one so the
                spread is in the picture's own units — the same glow at any
                size on screen. The region is grown to leave it room. */}
            {paint.bloom ? (
              <filter id={bloomId} x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation={referenceEdge * paint.bloom} />
                <feComponentTransfer>
                  <feFuncA type="linear" slope="1.6" />
                </feComponentTransfer>
              </filter>
            ) : null}
          </defs>
          {paint.bloom ? (
            // The cut-out again, underneath and blurred: the filter is on the
            // group so the glow bleeds *past* the clip rather than being cut
            // back to it.
            <g filter={`url(#${bloomId})`} data-hold-bloom="">
              <image
                href={imageSrc ?? undefined}
                x={0}
                y={0}
                width={W}
                height={H}
                preserveAspectRatio="none"
                clipPath={`url(#${colourClipId})`}
              />
            </g>
          ) : null}
          <image
            href={imageSrc ?? undefined}
            x={0}
            y={0}
            width={W}
            height={H}
            preserveAspectRatio="none"
            clipPath={`url(#${colourClipId})`}
            style={{ filter: paint.holds }}
          />
        </>
      )}

      <g ref={groupRef}>
        {visible.map(({ hold, on, dimmed }, i) => (
          <path
            key={i}
            // What tells a hold's mark from the arc of the hand that takes it,
            // and from any other path on the page — both are paths now.
            data-hold=""
            // The hold the run is heading for flashes, and is drawn heavier so it
            // is the one thing on the picture the eye lands on.
            className={on ? 'animate-pulse' : undefined}
            d={holdPath(hold, W, H)}
            fill={on ? `${hold.color || 'red'}` : 'none'}
            fillOpacity={on ? 0.35 : undefined}
            opacity={dimmed ? DIMMED_OPACITY : undefined}
            stroke={hold.color || 'red'}
            strokeWidth={
              holdStrokeWidth(hold, W, H, referenceEdge) * (on ? 2 : 1)
            }
            // pathLength normalizes the perimeter to 1 so the dash sweep (1 → 0)
            // traces the mark regardless of its real size or shape. Start hidden.
            pathLength={animate ? 1 : undefined}
            strokeDasharray={animate ? 1 : undefined}
            strokeDashoffset={animate ? 1 : undefined}
          />
        ))}
      </g>

      {/* The hand each hold is taken with, marked on its own side of the ring —
          an arc on the left for the left hand, on the right for the right, both
          for a match (see handMarkArc; `HandMark` is the same mark where the
          pictures are DOM). Over the rings, as thick as they are; keyed like
          their animation so it fades in with them rather than sitting on the
          wall before a single circle has been drawn. */}
      <g
        key={`hands-${mode}-${visibleCount}`}
        className={animate ? 'animate-in fade-in duration-700 motion-reduce:animate-none' : undefined}
      >
        {visible.flatMap(({ hold, hands, dimmed }, i) =>
          hands.map((hand) => (
            <path
              key={`${i}-${hand}`}
              data-hand={hand}
              d={arcOf(hold, W, H, hand)}
              fill="none"
              stroke={HAND_MARK_COLOR}
              strokeWidth={holdStrokeWidth(hold, W, H, referenceEdge)}
              strokeLinecap="butt"
              opacity={dimmed ? DIMMED_OPACITY : undefined}
            />
          ))
        )}
      </g>

      {/* The move each circle is, on a circuit — outside the animated group, so a
          number is never half-drawn. In the middle of the circle, in white with a
          dark outline: it is the one pair that reads on a photograph of any wall,
          and the ring is already carrying the role's colour. A foot wears none:
          the run is stood on it throughout rather than stopping there. */}
      {visible.map(({ hold, order, dimmed }, i) =>
        order ? (
          <text
            key={`order-${i}`}
            opacity={dimmed ? DIMMED_OPACITY : undefined}
            x={hold.x * W}
            y={hold.y * H}
            textAnchor="middle"
            dominantBaseline="central"
            fontWeight="bold"
            fontSize={holdNumberSize(hold, W, H, order)}
            fill="#ffffff"
            stroke="rgba(0,0,0,0.7)"
            strokeWidth={holdStrokeWidth(hold, W, H, referenceEdge)}
            paintOrder="stroke"
          >
            {order}
          </text>
        ) : null
      )}
    </svg>
  );
}

/** How much of itself a hold keeps while a run points somewhere else. */
const DIMMED_OPACITY = 0.3;

/**
 * The hand's arc of a hold's ellipse — `handMarkArc`'s degrees, which count
 * clockwise from the top, turned into two points on the ring and the arc
 * drawn clockwise between them.
 *
 * On a hold that knows its own shape this is the ellipse **inscribed in its
 * box**, so the mark sits beside the outline rather than on it. Close enough to
 * read which hand is meant, which is all it is for; a mark that followed the
 * shape would have to be a stroked slice of the outline itself.
 */
function arcOf(hold: Hold, W: number, H: number, hand: BoardHand): string {
  const cx = hold.x * W;
  const cy = hold.y * H;
  const rx = (hold.width / 2) * W;
  const ry = (hold.height / 2) * H;
  const { from, to } = handMarkArc(hand);
  // Clockwise from the top: x grows with the sine, y (downwards) shrinks with
  // the cosine.
  const point = (degrees: number) => {
    const radians = (degrees * Math.PI) / 180;
    return `${round(cx + rx * Math.sin(radians))} ${round(cy - ry * Math.cos(radians))}`;
  };

  return `M ${point(from)} A ${rx} ${ry} 0 0 1 ${point(to)}`;
}

/** To a thousandth of a viewBox unit — plenty on a photo, and a stable string. */
function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/**
 * How big a hold's number is drawn: as much of the circle as its digits will take
 * without spilling out of it. A hold the circuit matches on wears both of the
 * numbers it takes ("3,4"), which is three times the characters of a single one.
 */
function holdNumberSize(hold: Hold, W: number, H: number, order: string): number {
  const radius = Math.min((hold.width / 2) * W, (hold.height / 2) * H);

  return (radius * 1.6) / Math.max(2, order.length);
}
